import { Menu as MenuPrimitive } from '@base-ui/react'
import { Ellipsis } from 'lucide-react'

import { Button } from '@/components/ui/Button'
import { POPOVER_OPTION } from '@/components/ui/control'
import { cn } from '@/lib/cn'

/* A record header's «⋯» menu: the things about a record that are not part of
 * the work on it — its history, its revisions, its team — opened from one
 * trailing control instead of a tab each. Extracted from the quote's
 * (`QuoteMoreMenu`, S-3) when the case page needed the same control (S-11);
 * both pages render this one.
 *
 * Base UI's Menu, not a popover of buttons: it brings `role="menu"`, arrow-key
 * movement between the items, Escape and focus return, which a hand-made list
 * would have to re-implement. Portaled and positioned the way `ColumnsMenu`'s
 * popover is, at `z-[60]` — above the sheets' `z-50`, because a portaled popup
 * is their sibling and z-index alone decides which is seen.
 *
 * The menu OWNS NOTHING: it says which item was chosen, and the page opens
 * whatever that is. Each sheet's content is fetched by the sheet, so opening
 * the menu reads nothing.
 */

export interface MoreMenuItem<K extends string> {
  key: K
  label: string
}

export function MoreMenu<K extends string>({
  label,
  items,
  onSelect,
  size,
}: {
  /** The trigger's accessible name — it shows only the «⋯» glyph. */
  label: string
  items: readonly MoreMenuItem<K>[]
  onSelect: (key: K) => void
  /** The trigger's height, to match the row it ends. */
  size?: 'sm' | 'md'
}) {
  return (
    <MenuPrimitive.Root
      /* Not modal, for the reason the columns menu gives: a second scroll lock
         over the page's own is how a page ends up unscrollable after close. */
      modal={false}
    >
      <MenuPrimitive.Trigger
        render={
          <Button
            variant="secondary"
            size={size}
            aria-label={label}
            title={label}
            className={cn('shrink-0', size === 'sm' ? 'px-2' : 'px-2.5')}
          />
        }
      >
        <Ellipsis size={size === 'sm' ? 14 : 16} aria-hidden />
      </MenuPrimitive.Trigger>

      <MenuPrimitive.Portal>
        <MenuPrimitive.Positioner sideOffset={4} align="end" className="z-[60]">
          <MenuPrimitive.Popup
            className={cn(
              'bg-surface border-border-subtle min-w-[200px] rounded-md border p-1',
              'shadow-[var(--shadow-overlay)] focus:outline-none',
              'origin-(--transform-origin)',
              'transition-[opacity,transform] duration-[var(--dur-fast)]',
              'ease-[var(--ease-standard)]',
              'data-starting-style:scale-[0.98] data-starting-style:opacity-0',
              'data-ending-style:scale-[0.98] data-ending-style:opacity-0',
            )}
          >
            {items.map((item) => (
              <MenuPrimitive.Item
                key={item.key}
                onClick={() => onSelect(item.key)}
                className={cn(
                  POPOVER_OPTION,
                  'text-text-body data-highlighted:bg-surface-hover',
                )}
              >
                {item.label}
              </MenuPrimitive.Item>
            ))}
          </MenuPrimitive.Popup>
        </MenuPrimitive.Positioner>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Root>
  )
}
